import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { showNotification } from "services/NotificationService";
import { getErrorMessage } from "services/UtilityService";
import { formatDateForDisplay } from "services/MomentUtils";
import {
  cleanupTestDataBatch,
  fetchTestDataBatchGenerationResult,
  fetchTestDataBatches,
  fetchTestDataCatalog,
  generateTestDataBatch,
  uploadTestDataFax,
} from "services/TestDataService";
import "./TestData.css";

const DEFAULT_WF_ITEM_SCENARIO = "AWAITING_RESPONSE";

const formatScenarioOption = (s) =>
  `${s.label} — status ${s.wfItemStatus} / state ${s.wfItemState} (${s.eventCount} events)`;

const pickDefaultWfItemScenario = (scenarios) => {
  if (!scenarios?.length) return DEFAULT_WF_ITEM_SCENARIO;
  const preferred = scenarios.find((s) => s.name === DEFAULT_WF_ITEM_SCENARIO);
  return preferred?.name ?? scenarios[0].name;
};

const clientsForCustomer = (catalog, customerInfoId) => {
  if (!catalog?.clients || customerInfoId === "" || customerInfoId == null) {
    return [];
  }
  const id = Number(customerInfoId);
  return catalog.clients.filter((c) => c.customerInfoId === id);
};

const pickDefaultCustomerAndClient = (catalog, preferredCustomerId) => {
  const customers = catalog?.customers ?? [];
  if (!customers.length) {
    return { customerInfoId: "", clientInfoId: "" };
  }
  const customer =
    customers.find((c) => c.customerInfoId === preferredCustomerId) ??
    customers[0];
  const clients = clientsForCustomer(catalog, customer.customerInfoId);
  return {
    customerInfoId: String(customer.customerInfoId),
    clientInfoId: clients.length ? String(clients[0].clientInfoId) : "",
  };
};

const BatchGenerationResultTable = ({ result }) => (
  <>
    <p className="mb-2">
      <strong>Batch:</strong> <code>{result.testDataBatchUuid}</code>
      <br />
      <strong>Stage:</strong> {result.targetStage}{" "}
      <strong>Scenario:</strong> {result.scenario}
    </p>
    <Table responsive bordered size="sm" className="result-table mb-0">
      <thead>
        <tr>
          <th>#</th>
          <th>Claim</th>
          <th>Switch</th>
          <th>Wf item</th>
          <th>Call group</th>
          <th>Activity</th>
          <th>Phone</th>
        </tr>
      </thead>
      <tbody>
        {(result.items || []).map((item) => (
          <tr key={item.index}>
            <td>{item.index}</td>
            <td>{item.claimInfoId}</td>
            <td>{item.switchInfoId ?? "—"}</td>
            <td>{item.wfItemId ?? "—"}</td>
            <td>{item.callGroupId ?? "—"}</td>
            <td>{item.activityId ?? "—"}</td>
            <td>{item.prescriberPhone ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  </>
);

const emptyForm = (catalog) => {
  const stages = catalog?.stages ?? [];
  const wfScenarios = catalog?.wfItemScenarios ?? [];
  const defaultStage =
    stages.find((s) => s.name === "WF_ITEM")?.name ?? stages[0]?.name ?? "WF_ITEM";
  const { customerInfoId, clientInfoId } = pickDefaultCustomerAndClient(catalog);
  return {
    customerInfoId,
    clientInfoId,
    targetStage: defaultStage,
    count: 1,
    wfItemScenario: pickDefaultWfItemScenario(wfScenarios),
    createPrescriberCallActivities: false,
    forceSharedPrescriberPhone: false,
    notes: "",
  };
};

const TestData = () => {
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(true);

  const [form, setForm] = useState(() => emptyForm(null));
  const [generating, setGenerating] = useState(false);
  const [faxFiles, setFaxFiles] = useState([]);
  const [uploadingFax, setUploadingFax] = useState(false);
  const [uploadedFaxes, setUploadedFaxes] = useState(null);
  const [selectedBatchUuid, setSelectedBatchUuid] = useState(null);
  const [batchResult, setBatchResult] = useState(null);
  const [batchResultLoading, setBatchResultLoading] = useState(false);
  const [batchResultError, setBatchResultError] = useState(null);

  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [batchStatusFilter, setBatchStatusFilter] = useState("ACTIVE");
  const [cleaningUuid, setCleaningUuid] = useState(null);

  const showWfItemScenario = form.targetStage === "WF_ITEM";

  const scenarioOptions = useMemo(() => {
    if (!catalog?.wfItemScenarios) return [];
    return catalog.wfItemScenarios;
  }, [catalog]);

  const scenarioNames = useMemo(
    () => new Set(scenarioOptions.map((s) => s.name)),
    [scenarioOptions]
  );

  const customerOptions = useMemo(
    () => catalog?.customers ?? [],
    [catalog]
  );

  const clientOptions = useMemo(
    () => clientsForCustomer(catalog, form.customerInfoId),
    [catalog, form.customerInfoId]
  );

  const loadCatalog = useCallback(() => {
    setCatalogLoading(true);
    setCatalogError(null);
    fetchTestDataCatalog(
      (response) => {
        const data = response.data;
        setCatalog(data);
        setForm((prev) => {
          const next = { ...prev };
          const stageNames = new Set((data?.stages ?? []).map((s) => s.name));
          if (!stageNames.has(next.targetStage)) {
            next.targetStage =
              data?.stages?.find((s) => s.name === "WF_ITEM")?.name ??
              data?.stages?.[0]?.name ??
              "WF_ITEM";
          }
          const scenarios = data?.wfItemScenarios ?? [];
          if (
            !scenarios.some((s) => s.name === next.wfItemScenario) &&
            scenarios.length
          ) {
            next.wfItemScenario = pickDefaultWfItemScenario(scenarios);
          }
          const customers = data?.customers ?? [];
          const prevCustomerId = Number(next.customerInfoId);
          const customerStillValid = customers.some(
            (c) => c.customerInfoId === prevCustomerId
          );
          if (!customerStillValid && customers.length) {
            const defaults = pickDefaultCustomerAndClient(data);
            next.customerInfoId = defaults.customerInfoId;
            next.clientInfoId = defaults.clientInfoId;
          } else {
            const clients = clientsForCustomer(data, next.customerInfoId);
            const prevClientId = Number(next.clientInfoId);
            if (!clients.some((c) => c.clientInfoId === prevClientId)) {
              next.clientInfoId = clients.length
                ? String(clients[0].clientInfoId)
                : "";
            }
          }
          return next;
        });
        setCatalogLoading(false);
      },
      (err) => {
        setCatalog(null);
        setCatalogLoading(false);
        const status = err?.status;
        setCatalogError(
          status === 404
            ? "Test data API is not available. Ensure app.testdata.enabled=true on the backend."
            : getErrorMessage(err?.data) || "Could not load test data catalog."
        );
      }
    );
  }, []);

  const loadBatchResult = useCallback((batchUuid) => {
    setSelectedBatchUuid(batchUuid);
    setBatchResultLoading(true);
    setBatchResultError(null);
    setBatchResult(null);
    fetchTestDataBatchGenerationResult(
      batchUuid,
      (response) => {
        setBatchResult(response.data);
        setBatchResultLoading(false);
      },
      (err) => {
        setBatchResultLoading(false);
        setBatchResult(null);
        setBatchResultError(
          err?.status === 404
            ? "No stored IDs for this batch (older runs may not have saved results)."
            : getErrorMessage(err?.data) || "Could not load batch result."
        );
      }
    );
  }, []);

  const handleBatchRowClick = (batchUuid) => {
    if (batchUuid === selectedBatchUuid && batchResult && !batchResultLoading) {
      return;
    }
    loadBatchResult(batchUuid);
  };

  const loadBatches = useCallback(() => {
    setBatchesLoading(true);
    fetchTestDataBatches(
      batchStatusFilter,
      (response) => {
        setBatches(response.data || []);
        setBatchesLoading(false);
      },
      (err) => {
        setBatches([]);
        setBatchesLoading(false);
        showNotification({
          title: "Error",
          message: getErrorMessage(err?.data) || "Could not load batches.",
          type: "error",
        });
      }
    );
  }, [batchStatusFilter]);

  useEffect(() => {
    setSelectedBatchUuid(null);
    setBatchResult(null);
    setBatchResultError(null);
  }, [batchStatusFilter]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    if (!catalogError) {
      loadBatches();
    }
  }, [loadBatches, catalogError]);

  const updateField = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "customerInfoId") {
        const clients = clientsForCustomer(catalog, value);
        next.clientInfoId = clients.length ? String(clients[0].clientInfoId) : "";
      }
      if (field === "targetStage") {
        if (value !== "WF_ITEM") {
          next.createPrescriberCallActivities = false;
          next.forceSharedPrescriberPhone = false;
        } else if (!scenarioNames.has(next.wfItemScenario)) {
          next.wfItemScenario = pickDefaultWfItemScenario(scenarioOptions);
        }
      }
      return next;
    });
  };

  const handleGenerate = (e) => {
    e.preventDefault();
    const customerInfoId = Number(form.customerInfoId);
    const clientInfoId = Number(form.clientInfoId);
    const count = Number(form.count);

    if (!Number.isFinite(customerInfoId) || customerInfoId <= 0) {
      showNotification({
        title: "Validation",
        message: "Select a customer.",
        type: "warning",
      });
      return;
    }
    if (!Number.isFinite(clientInfoId) || clientInfoId <= 0) {
      showNotification({
        title: "Validation",
        message: "Select a client for the chosen customer.",
        type: "warning",
      });
      return;
    }
    if (!clientOptions.some((c) => c.clientInfoId === clientInfoId)) {
      showNotification({
        title: "Validation",
        message: "Selected client does not belong to the chosen customer.",
        type: "warning",
      });
      return;
    }
    if (!Number.isInteger(count) || count < 1 || count > 25) {
      showNotification({
        title: "Validation",
        message: "Count must be between 1 and 25.",
        type: "warning",
      });
      return;
    }

    const body = {
      customerInfoId,
      clientInfoId,
      targetStage: form.targetStage,
      count,
      createPrescriberCallActivities: form.createPrescriberCallActivities,
      forceSharedPrescriberPhone: form.forceSharedPrescriberPhone,
      notes: form.notes?.trim() || null,
    };
    if (showWfItemScenario) {
      if (!scenarioNames.has(form.wfItemScenario)) {
        showNotification({
          title: "Validation",
          message: "Select a wf item scenario from the catalog.",
          type: "warning",
        });
        return;
      }
      body.wfItemScenario = form.wfItemScenario;
    }

    setGenerating(true);
    generateTestDataBatch(
      body,
      (response) => {
        setGenerating(false);
        const batchUuid = response.data?.testDataBatchUuid;
        setSelectedBatchUuid(batchUuid ?? null);
        setBatchResult(response.data ?? null);
        setBatchResultError(null);
        setBatchResultLoading(false);
        showNotification({
          title: "Success",
          message: `Created batch ${batchUuid}`,
          type: "success",
        });
        loadBatches();
      },
      (err) => {
        setGenerating(false);
        showNotification({
          title: "Error",
          message: getErrorMessage(err?.data) || "Generation failed.",
          type: "error",
        });
      }
    );
  };

  const handleCleanup = (batchUuid) => {
    if (
      !window.confirm(
        "Delete all domain rows tracked for this batch? This cannot be undone."
      )
    ) {
      return;
    }
    setCleaningUuid(batchUuid);
    cleanupTestDataBatch(
      batchUuid,
      (response) => {
        setCleaningUuid(null);
        if (selectedBatchUuid === batchUuid) {
          setSelectedBatchUuid(null);
          setBatchResult(null);
          setBatchResultError(null);
        }
        showNotification({
          title: "Cleaned up",
          message: `Removed ${response.data?.domainRowsDeleted ?? 0} tracked row(s).`,
          type: "success",
        });
        loadBatches();
      },
      (err) => {
        setCleaningUuid(null);
        showNotification({
          title: "Error",
          message: getErrorMessage(err?.data) || "Cleanup failed.",
          type: "error",
        });
      }
    );
  };

  const handleFaxUpload = (e) => {
    e.preventDefault();
    if (!faxFiles.length) {
      showNotification({
        title: "Validation",
        message: "Select at least one PDF file.",
        type: "warning",
      });
      return;
    }

    const body = new FormData();
    faxFiles.forEach((file) => body.append("files", file));

    setUploadingFax(true);
    uploadTestDataFax(
      body,
      (response) => {
        setUploadingFax(false);
        const uploaded = response.data || [];
        setUploadedFaxes(uploaded);
        setFaxFiles([]);
        showNotification({
          title: "Fax uploaded",
          message: `Created ${uploaded.length} unprocessed fax${
            uploaded.length === 1 ? "" : "es"
          }`,
          type: "success",
        });
      },
      (err) => {
        setUploadingFax(false);
        showNotification({
          title: "Error",
          message: getErrorMessage(err?.data) || "Fax upload failed.",
          type: "error",
        });
      }
    );
  };

  if (catalogLoading) {
    return (
      <Container fluid className="test-data-page top-spacer text-center py-5">
        <Spinner animation="border" role="status" />
        <p className="mt-2 text-muted">Loading test data catalog…</p>
      </Container>
    );
  }

  if (catalogError) {
    return (
      <Container fluid className="test-data-page top-spacer">
        <Alert variant="danger">
          <Alert.Heading>Test data factory unavailable</Alert.Heading>
          <p>{catalogError}</p>
          <Button variant="outline-danger" size="sm" onClick={loadCatalog}>
            Retry
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="test-data-page top-spacer">
      <Row className="mb-3">
        <Col>
          <h4>Test Data Factory</h4>
          <p className="text-muted mb-0">
            Generate UAT workflow data by stage. Select an existing customer and
            client from the database.
          </p>
        </Col>
      </Row>

      <Row>
        <Col lg={5}>
          <Card className="section-card">
            <Card.Header>Generate</Card.Header>
            <Card.Body>
              <Form onSubmit={handleGenerate}>
                <Form.Group className="mb-3">
                  <Form.Label>Customer</Form.Label>
                  <Form.Select
                    value={form.customerInfoId}
                    onChange={(e) => updateField("customerInfoId", e.target.value)}
                    required
                    disabled={!customerOptions.length}
                  >
                    {!customerOptions.length && (
                      <option value="">No customers available</option>
                    )}
                    {customerOptions.map((c) => (
                      <option key={c.customerInfoId} value={String(c.customerInfoId)}>
                        {c.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Client</Form.Label>
                  <Form.Select
                    value={form.clientInfoId}
                    onChange={(e) => updateField("clientInfoId", e.target.value)}
                    required
                    disabled={!form.customerInfoId || !clientOptions.length}
                  >
                    {!form.customerInfoId && (
                      <option value="">Select a customer first</option>
                    )}
                    {form.customerInfoId && !clientOptions.length && (
                      <option value="">No clients for this customer</option>
                    )}
                    {clientOptions.map((c) => (
                      <option key={c.clientInfoId} value={String(c.clientInfoId)}>
                        {c.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Target stage</Form.Label>
                  <Form.Select
                    value={form.targetStage}
                    onChange={(e) => updateField("targetStage", e.target.value)}
                  >
                    {(catalog?.stages || []).map((s) => (
                      <option key={s.name} value={s.name}>
                        {s.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {catalog?.stages?.find((s) => s.name === form.targetStage)
                      ?.description}
                  </Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Count</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    max={25}
                    value={form.count}
                    onChange={(e) => updateField("count", e.target.value)}
                    required
                  />
                </Form.Group>
                {showWfItemScenario && (
                  <Form.Group className="mb-3">
                    <Form.Label>Wf item scenario</Form.Label>
                    <Form.Select
                      value={form.wfItemScenario}
                      onChange={(e) =>
                        updateField("wfItemScenario", e.target.value)
                      }
                    >
                      {scenarioOptions.map((s) => (
                        <option key={s.name} value={s.name}>
                          {formatScenarioOption(s)}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                )}
                <Form.Group className="mb-2">
                  <Form.Check
                    type="checkbox"
                    id="createPrescriberCallActivities"
                    label="Create prescriber call activities"
                    checked={form.createPrescriberCallActivities}
                    onChange={(e) =>
                      updateField(
                        "createPrescriberCallActivities",
                        e.target.checked
                      )
                    }
                    disabled={!showWfItemScenario}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="forceSharedPrescriberPhone"
                    label="Force shared prescriber phone (one call group)"
                    checked={form.forceSharedPrescriberPhone}
                    onChange={(e) =>
                      updateField("forceSharedPrescriberPhone", e.target.checked)
                    }
                    disabled={!showWfItemScenario}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Notes (optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={form.notes}
                    onChange={(e) => updateField("notes", e.target.value)}
                  />
                </Form.Group>
                <Button type="submit" variant="primary" disabled={generating}>
                  {generating ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Generating…
                    </>
                  ) : (
                    "Generate"
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>

          <Card className="section-card">
            <Card.Header>Upload Fax</Card.Header>
            <Card.Body>
              <Form onSubmit={handleFaxUpload}>
                <Form.Group className="mb-3">
                  <Form.Label>PDF faxes</Form.Label>
                  <Form.Control
                    type="file"
                    accept="application/pdf,.pdf"
                    multiple
                    onChange={(e) =>
                      setFaxFiles(Array.from(e.target.files ?? []))
                    }
                    required
                  />
                  <Form.Text className="text-muted">
                    Select one or more PDFs. Each creates an UNPROCESSED fax
                    without OCR or workflow state changes.
                  </Form.Text>
                </Form.Group>
                {faxFiles.length > 0 && (
                  <ul className="small text-muted mb-3">
                    {faxFiles.map((file) => (
                      <li key={file.name}>{file.name}</li>
                    ))}
                  </ul>
                )}
                <Button type="submit" variant="primary" disabled={uploadingFax}>
                  {uploadingFax ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Uploading…
                    </>
                  ) : (
                    "Upload faxes"
                  )}
                </Button>
              </Form>
              {uploadedFaxes && uploadedFaxes.length > 0 && (
                <Alert variant="success" className="mt-3 mb-0">
                  <div className="mb-2">
                    <strong>
                      Created {uploadedFaxes.length} unprocessed fax
                      {uploadedFaxes.length === 1 ? "" : "es"}:
                    </strong>
                  </div>
                  <ul className="mb-2">
                    {uploadedFaxes.map((fax) => (
                      <li key={fax.faxId}>
                        <code>{fax.faxInfoUuid}</code>
                      </li>
                    ))}
                  </ul>
                  <a href="/unprocessed-fax/program-type-list">
                    Open Unprocessed Fax List
                  </a>
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={7}>
          <Card className="section-card">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>My batches</span>
              <div className="d-flex gap-2 align-items-center">
                <Form.Select
                  size="sm"
                  style={{ width: "auto" }}
                  value={batchStatusFilter}
                  onChange={(e) => setBatchStatusFilter(e.target.value)}
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="CLEANED_UP">CLEANED_UP</option>
                  <option value="FAILED">FAILED</option>
                </Form.Select>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={loadBatches}
                  disabled={batchesLoading}
                >
                  Refresh
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <p className="text-muted small px-3 pt-2 mb-0">
                Click a row to view generated IDs.
              </p>
              {batchesLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : batches.length === 0 ? (
                <p className="text-muted p-3 mb-0">No batches found.</p>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th>Created</th>
                      <th>Stage</th>
                      <th>Scenario</th>
                      <th>Count</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {batches.map((b) => (
                      <tr
                        key={b.testDataBatchUuid}
                        className={
                          selectedBatchUuid === b.testDataBatchUuid
                            ? "table-active batch-row"
                            : "batch-row"
                        }
                        onClick={() => handleBatchRowClick(b.testDataBatchUuid)}
                      >
                        <td>
                          {b.creationDate
                            ? formatDateForDisplay(b.creationDate)
                            : "—"}
                        </td>
                        <td>{b.stage}</td>
                        <td>{b.scenario}</td>
                        <td>{b.requestedCount}</td>
                        <td>
                          <Badge
                            bg={
                              b.status === "ACTIVE"
                                ? "success"
                                : b.status === "FAILED"
                                  ? "danger"
                                  : "secondary"
                            }
                          >
                            {b.status}
                          </Badge>
                        </td>
                        <td className="batch-actions text-end">
                          {b.status === "ACTIVE" && (
                            <Button
                              size="sm"
                              variant="outline-danger"
                              disabled={cleaningUuid === b.testDataBatchUuid}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCleanup(b.testDataBatchUuid);
                              }}
                            >
                              {cleaningUuid === b.testDataBatchUuid ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                "Cleanup"
                              )}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          {selectedBatchUuid && (
            <Card className="section-card">
              <Card.Header>Generated IDs</Card.Header>
              <Card.Body>
                {batchResultLoading && (
                  <div className="text-center py-3">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading…
                  </div>
                )}
                {!batchResultLoading && batchResultError && (
                  <Alert variant="warning" className="mb-0">
                    {batchResultError}
                  </Alert>
                )}
                {!batchResultLoading && batchResult && (
                  <BatchGenerationResultTable result={batchResult} />
                )}
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default TestData;
